import React, { useMemo, useState } from 'react';
import useTables from './hooks/useTables';
import useTableCart from './hooks/useTableCart';

const statusClasses = {
  available: 'bg-green-100 border-green-300 text-green-800',
  occupied: 'bg-red-100 border-red-300 text-red-800',
  reserved: 'bg-blue-100 border-blue-300 text-blue-800',
  merged: 'bg-yellow-100 border-yellow-300 text-yellow-900',
};

const TableManagementModal = ({ onClose, storeId = '' }) => {
  const {
    tables,
    stores,
    selectedStoreId,
    setSelectedStoreId,
    loading,
    actionLoading,
    error,
    successMessage,
    mergeTables,
    moveTable,
    splitTable,
  } = useTables(storeId);

  const {
    currentCart,
    cartLoading,
    cartError,
    fetchCartByTable,
    clearCart,
  } = useTableCart();

  const [selectedTableId, setSelectedTableId] = useState(null);
  const [targetTableId, setTargetTableId] = useState('');

  const selectedTable = useMemo(
    () => (Array.isArray(tables) ? tables.find((table) => table._id === selectedTableId) : null),
    [tables, selectedTableId]
  );

  const sortedTables = useMemo(() => {
    const list = Array.isArray(tables) ? [...tables] : [];
    list.sort((a, b) => String(a.tableNumber).localeCompare(String(b.tableNumber), undefined, { numeric: true }));
    return list;
  }, [tables]);

  const availableTargets = useMemo(
    () => sortedTables.filter((table) => table._id !== selectedTableId),
    [sortedTables, selectedTableId]
  );

  const canMerge = Boolean(selectedTable && targetTableId && (selectedTable.status === 'occupied' || selectedTable.status === 'merged'));
  const canMove = Boolean(selectedTable && targetTableId && (selectedTable.status === 'occupied' || selectedTable.status === 'merged'));
  const canSplit = Boolean(selectedTable && (selectedTable.status === 'merged' || (selectedTable.mergedWith || []).length > 0));

  const handleTableClick = async (table) => {
    setSelectedTableId(table._id);
    setTargetTableId('');
    await fetchCartByTable(table.tableNumber);
  };

  const resetSelection = () => {
    setSelectedTableId(null);
    setTargetTableId('');
    clearCart();
  };

  const handleMerge = async () => {
    if (!canMerge || !selectedTableId || !targetTableId) return;
    await mergeTables({ sourceTableId: selectedTableId, targetTableId });
    resetSelection();
  };

  const handleMove = async () => {
    if (!canMove || !selectedTableId || !targetTableId) return;
    await moveTable({ sourceTableId: selectedTableId, targetTableId });
    resetSelection();
  };

  const handleSplit = async () => {
    if (!canSplit || !selectedTableId) return;
    await splitTable({ tableId: selectedTableId });
    resetSelection();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
      <div className="bg-white w-full max-w-6xl p-6 rounded-lg shadow-lg max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h1 className="text-2xl font-bold">Table Management</h1>
          <button className="text-red-500 hover:text-red-700 font-semibold" onClick={onClose}>Close</button>
        </div>

        {stores.length > 0 && (
          <div className="mb-4">
            <label className="block text-sm font-medium mb-1">Store</label>
            <select
              value={selectedStoreId}
              onChange={(e) => {
                setSelectedStoreId(e.target.value);
                resetSelection();
              }}
              className="border rounded-md px-3 py-2 text-sm"
            >
              <option value="">All Stores</option>
              {stores.map((store) => (
                <option key={store._id} value={store._id}>
                  {store.name || store.storeName || store._id}
                </option>
              ))}
            </select>
          </div>
        )}

        {error && <div className="mb-4 p-2 bg-red-100 text-red-700 border border-red-200 rounded">{error}</div>}
        {successMessage && <div className="mb-4 p-2 bg-green-100 text-green-700 border border-green-200 rounded">{successMessage}</div>}

        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
          {loading ? (
            <div className="col-span-full text-center py-8 text-gray-500">Loading tables...</div>
          ) : sortedTables.length === 0 ? (
            <div className="col-span-full text-center py-8 text-gray-500">No tables found.</div>
          ) : (
            sortedTables.map((table) => {
              const isSelected = selectedTableId === table._id;
              const isMerged = Array.isArray(table.mergedWith) && table.mergedWith.length > 0;

              return (
                <button
                  key={table._id}
                  type="button"
                  onClick={() => handleTableClick(table)}
                  className={`relative rounded-lg border p-3 text-left transition ${statusClasses[table.status] || 'bg-gray-100 border-gray-300 text-gray-700'} ${isSelected ? 'ring-2 ring-teal-500' : ''}`}
                >
                  <div className="font-semibold">Table {table.tableNumber}</div>
                  <div className="text-xs uppercase tracking-wide">{table.status}</div>
                  <div className="text-xs">Seats: {table.seats || 0}</div>
                  <div className="text-xs">Floor: {table.floor || 1}</div>
                  {isMerged && (
                    <div className="mt-1 text-[11px] font-medium">
                      Merged: {table.mergedWith.join(', ')}
                    </div>
                  )}
                </button>
              );
            })
          )}
        </div>

        {selectedTable && (
          <div className="mt-6 border-t pt-4">
            <h2 className="text-lg font-bold">Selected Table: {selectedTable.tableNumber}</h2>
            {cartLoading && <p className="text-sm text-gray-500 mt-2">Loading cart...</p>}
            {cartError && <p className="text-sm text-red-600 mt-2">{cartError}</p>}

            {currentCart ? (
              <div className="mt-3 text-sm text-gray-800 space-y-1">
                <div>
                  Active Cart: <span className="font-semibold">{currentCart.transactionNumber || currentCart._id}</span>
                </div>
                <div>Transaction Type: <span className="font-semibold">{currentCart.transactionType || 'N/A'}</span></div>
              </div>
            ) : (
              <p className="mt-2 text-sm text-gray-500">No active cart on this table.</p>
            )}

            <div className="mt-4">
              <label className="block text-sm font-medium mb-2">Target Table (for merge/move)</label>
              <select
                value={targetTableId}
                onChange={(e) => setTargetTableId(e.target.value)}
                className="border rounded-md p-2 min-w-[250px]"
              >
                <option value="">Select target table</option>
                {availableTargets.map((table) => (
                  <option key={table._id} value={table._id}>
                    Table {table.tableNumber} ({table.status})
                  </option>
                ))}
              </select>
            </div>

            <div className="mt-4 flex flex-wrap gap-3">
              {canMerge && (
                <button
                  type="button"
                  onClick={handleMerge}
                  disabled={actionLoading}
                  className="bg-teal-600 text-white px-4 py-2 rounded hover:bg-teal-700 disabled:opacity-50"
                >
                  Merge
                </button>
              )}

              {canMove && (
                <button
                  type="button"
                  onClick={handleMove}
                  disabled={actionLoading}
                  className="bg-yellow-500 text-white px-4 py-2 rounded hover:bg-yellow-600 disabled:opacity-50"
                >
                  Move
                </button>
              )}

              {canSplit && (
                <button
                  type="button"
                  onClick={handleSplit}
                  disabled={actionLoading}
                  className="bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700 disabled:opacity-50"
                >
                  Split
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TableManagementModal;
